// macadamia-app/src/layout/ProfilePictureManager.tsx
import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

interface ProfilePictureManagerProps {
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    editable?: boolean;
}

const ProfilePictureManager = memo(({
    size = 'md',
    className = '',
    editable = false
}: ProfilePictureManagerProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { user, setUser, refreshProfilePicture } = useAuth();

    // Refs pour éviter les boucles infinies
    const lastRefreshAttempt = useRef<number>(0);
    const refreshInProgress = useRef<boolean>(false);

    const sizes = {
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'h-16 w-16'
    };

    // OPTIMISATION : Mapping taille → preset
    const getSizePreset = (size: 'sm' | 'md' | 'lg'): string => {
        const presetMap = {
            sm: 'thumbnail',  // 64x64, quality 75
            md: 'avatar',     // 96x96, quality 80, webp
            lg: 'small'       // 128x128, quality 80
        };
        return presetMap[size];
    };

    // Fonction mémorisée pour rafraîchir la photo de profil avec optimisation
    const refreshProfilePictureLocal = useCallback(async () => {
        // SI PAS DE PHOTO DE PROFIL, NE RIEN FAIRE - STOPPER LA BOUCLE
        if (!user?.profilePicture || refreshInProgress.current) return null;

        const now = Date.now();
        const preset = getSizePreset(size);

        // Éviter les appels trop fréquents (minimum 10 secondes entre les tentatives)
        if (now - lastRefreshAttempt.current < 10000) {
            return user?.profilePictureUrl || null;
        }

        // Vérifier si l'URL est encore valide ET correspond au bon preset
        if (
            user.profilePictureUrl &&
            user.profilePictureTimestamp &&
            user.profilePictureCache === `${user.profilePicture}-${preset}` &&
            now - user.profilePictureTimestamp < 5 * 60 * 1000
        ) {
            return user.profilePictureUrl;
        }

        refreshInProgress.current = true;
        lastRefreshAttempt.current = now;

        try {
            // APPEL OPTIMISÉ avec preset
            const imageUrl = await refreshProfilePicture(preset);

            if (imageUrl) {
                setError(false);
                return imageUrl;
            }

            // Pas d'image trouvée - NE PAS effacer profilePicture (la clé)
            // Effacer seulement l'URL en cache pour permettre un retry
            setUser((prev) =>
                prev ? {
                    ...prev,
                    // profilePicture: '', // NE PAS EFFACER LA CLÉ !
                    profilePictureUrl: undefined,
                    profilePictureTimestamp: undefined,
                    profilePictureCache: undefined,
                } : null
            );

            return null;
        } catch (error) {
            console.error("Erreur lors du rafraîchissement de la photo de profil:", error);

            // En cas d'erreur, NE PAS effacer profilePicture pour permettre un retry
            setUser((prev) =>
                prev ? {
                    ...prev,
                    // profilePicture: '', // NE PAS EFFACER LA CLÉ !
                    profilePictureUrl: undefined,
                    profilePictureTimestamp: undefined,
                    profilePictureCache: undefined,
                } : null
            );

            setError(true);
            return null;
        } finally {
            refreshInProgress.current = false;
        }
    }, [user?.profilePicture, user?.profilePictureUrl, user?.profilePictureTimestamp, user?.profilePictureCache, setUser, refreshProfilePicture, size]);

    // Rafraîchissement initial uniquement si nécessaire
    useEffect(() => {
        if (!user?.profilePicture) return;

        const preset = getSizePreset(size);
        const expectedCacheKey = `${user.profilePicture}-${preset}`;

        const shouldRefresh = !user?.profilePictureUrl ||
            !user?.profilePictureTimestamp ||
            user?.profilePictureCache !== expectedCacheKey || // Preset différent
            (Date.now() - user.profilePictureTimestamp > 5 * 60 * 1000);

        if (shouldRefresh) {
            refreshProfilePictureLocal();
        }
    }, [user?.profilePicture, size]); // Re-déclencher si la taille change

    // Gestionnaire pour les erreurs de chargement d'image
    const handleImageError = useCallback(() => {
        if (!error && !refreshInProgress.current) {
            setError(true);
            // Attendre un peu avant de tenter un refresh
            setTimeout(() => {
                refreshProfilePictureLocal();
            }, 2000);
        }
    }, [error, refreshProfilePictureLocal]);

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files?.[0]) return;

        try {
            setIsLoading(true);
            setError(false);

            const file = event.target.files[0];
            if (file.size > 5 * 1024 * 1024) {
                throw new Error('Image trop volumineuse (max 5MB)');
            }

            const formData = new FormData();
            formData.append('image', file);

            const API_URL = process.env.REACT_APP_URL_API_MACADAMIA;
            const response = await fetch(`${API_URL}/users/profile-picture`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}: ${await response.text()}`);
            }

            const data = await response.json();

            setUser(prev => prev ? {
                ...prev,
                profilePicture: data.profilePictureKey,
                profilePictureUrl: undefined,
                profilePictureTimestamp: undefined,
                profilePictureCache: undefined // Reset cache
            } : null);

            // Rafraîchir après upload avec le bon preset
            setTimeout(() => {
                refreshProfilePictureLocal();
            }, 1000);

        } catch (err) {
            console.error('Erreur lors de l\'upload:', err);
            setError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const getInitials = () => {
        if (!user?.name) return '';
        return user.name.split(' ')
            .map(part => part[0]?.toUpperCase())
            .slice(0, 2)
            .join('');
    };

    return (
        <div className={`relative ${sizes[size]} ${className}`}>
            {isLoading && (
                <div className="absolute inset-0 bg-gray-200 dark:bg-mcdm-primary-100 animate-pulse rounded-full z-20" />
            )}

            <div className="absolute inset-0 rounded-full border border-gray-200 dark:border-gray-600 
                          hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-200" />

            <div
                className={`relative h-full w-full rounded-full overflow-hidden 
                          ${editable ? 'cursor-pointer group' : ''}`}
                onClick={() => editable && fileInputRef.current?.click()}
            >
                {user?.profilePictureUrl && !error ? (
                    <img
                        src={user.profilePictureUrl}
                        alt={user?.name ?? 'Profil utilisateur'}
                        className="h-full w-full object-cover"
                        onError={handleImageError}
                    />
                ) : (
                    <div className="h-full w-full bg-mcdm-third-200 dark:bg-mcdm-primary-100 
                                  text-white dark:text-mcdm-third-500 
                                  flex items-center justify-center text-sm font-medium">
                        {getInitials()}
                    </div>
                )}

                {editable && (
                    <>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            className="hidden"
                            accept="image/*"
                        />
                        <div className="absolute inset-0 flex items-center justify-center 
                                      bg-black dark:bg-black bg-opacity-0 dark:bg-opacity-0 
                                      group-hover:bg-opacity-30 dark:group-hover:bg-opacity-40 
                                      transition-all rounded-full z-10">
                            <svg
                                className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
});

ProfilePictureManager.displayName = 'ProfilePictureManager';

export default ProfilePictureManager;